<template>
  <div class="sc-page" v-loading="loading">
    <div class="sc-title">系统设置</div>

    <!-- 会话建立限流 -->
    <el-card shadow="never" class="sc-card">
      <div slot="header" class="sc-card-head">
        <span>会话建立限流</span>
        <span class="sc-hint">同一 IP 在指定秒数内最多能建立几个会话，超过即触发限流</span>
      </div>
      <el-row :gutter="16">
        <el-col :span="12">
          <div class="sc-field-label">每窗口最多建立会话数（同 IP）</div>
          <el-input-number v-model="rateSession.max" :min="1" :max="9999" class="full-width" />
        </el-col>
        <el-col :span="12">
          <div class="sc-field-label">窗口时长（秒）</div>
          <el-input-number v-model="rateSession.window" :min="1" :max="86400" class="full-width" />
        </el-col>
      </el-row>
      <el-button type="primary" class="sc-save" :loading="saving === RATE_SESSION_ID" @click="saveRateLimit(RATE_SESSION_ID, 'rate_limit.session.max_per_ip', 'rate_limit.session.window_seconds', rateSession)">
        <i class="el-icon-document-checked"></i> 保存
      </el-button>
    </el-card>

    <!-- 消息发送限流 -->
    <el-card shadow="never" class="sc-card">
      <div slot="header" class="sc-card-head">
        <span>消息发送限流</span>
        <span class="sc-hint">同一会话在指定秒数内最多能发几条消息，超过即触发限流</span>
      </div>
      <el-row :gutter="16">
        <el-col :span="12">
          <div class="sc-field-label">每窗口最多发送消息数（同会话）</div>
          <el-input-number v-model="rateMessage.max" :min="1" :max="9999" class="full-width" />
        </el-col>
        <el-col :span="12">
          <div class="sc-field-label">窗口时长（秒）</div>
          <el-input-number v-model="rateMessage.window" :min="1" :max="86400" class="full-width" />
        </el-col>
      </el-row>
      <el-button type="primary" class="sc-save" :loading="saving === RATE_MESSAGE_ID" @click="saveRateLimit(RATE_MESSAGE_ID, 'rate_limit.message.max_per_session', 'rate_limit.message.window_seconds', rateMessage)">
        <i class="el-icon-document-checked"></i> 保存
      </el-button>
    </el-card>

    <!-- 自动封禁 -->
    <el-card shadow="never" class="sc-card">
      <div slot="header" class="sc-card-head">
        <span>自动封禁</span>
        <span class="sc-hint">限流被触发达到指定次数后，自动将该 IP 加入黑名单</span>
      </div>
      <el-row :gutter="16">
        <el-col :span="8">
          <div class="sc-field-label">累计触发限流次数</div>
          <el-input-number v-model="autoBan.triggerCount" :min="1" :max="100" class="full-width" />
        </el-col>
        <el-col :span="8">
          <div class="sc-field-label">统计窗口（分钟）</div>
          <el-input-number v-model="autoBan.triggerWindow" :min="1" :max="1440" class="full-width" />
        </el-col>
        <el-col :span="8">
          <div class="sc-field-label">封禁时长（小时）</div>
          <el-input-number v-model="autoBan.duration" :min="1" :max="8760" class="full-width" />
        </el-col>
      </el-row>
      <el-button type="primary" class="sc-save" :loading="saving === AUTO_BAN_ID" @click="saveAutoBan">
        <i class="el-icon-document-checked"></i> 保存
      </el-button>
    </el-card>

    <!-- 验证码设置 -->
    <el-card shadow="never" class="sc-card">
      <div slot="header" class="sc-card-head">
        <span>验证码设置</span>
        <span class="sc-hint">控制客户端是否需要通过验证码才能操作</span>
      </div>
      <el-row :gutter="24">
        <el-col :span="12">
          <div class="sc-field-label">全局验证码（建立会话前必须验证）</div>
          <el-switch v-model="captcha.globalEnabled" active-text="开" inactive-text="关" />
        </el-col>
        <el-col :span="12">
          <div class="sc-field-label">限流接近时弹出验证码</div>
          <el-switch v-model="captcha.triggerOnRateLimit" active-text="开" inactive-text="关" />
        </el-col>
      </el-row>
      <el-button type="primary" class="sc-save" :loading="saving === CAPTCHA_ID" @click="saveCaptcha">
        <i class="el-icon-document-checked"></i> 保存
      </el-button>
    </el-card>

    <!-- 客服转接设置 -->
    <el-card shadow="never" class="sc-card">
      <div slot="header" class="sc-card-head">
        <span>客服转接设置</span>
        <span class="sc-hint">控制 AI 对话多少轮后显示「转人工客服」按钮</span>
      </div>
      <el-row :gutter="16">
        <el-col :span="12">
          <div class="sc-field-label">AI 对话轮次门槛（达到此轮次后显示转人工按钮）</div>
          <el-input-number v-model="minReplies" :min="1" :max="20" class="full-width" />
        </el-col>
      </el-row>
      <el-button type="primary" class="sc-save" :loading="saving === 'chat.min_ai_replies_for_agent'" @click="saveMinReplies">
        <i class="el-icon-document-checked"></i> 保存
      </el-button>
    </el-card>

    <!-- 会话闲置超时 -->
    <el-card shadow="never" class="sc-card">
      <div slot="header" class="sc-card-head">
        <span>会话闲置超时</span>
        <span class="sc-hint">客户在任何对话状态下闲置超过此时间后，系统将自动结束对话</span>
      </div>
      <el-row :gutter="16">
        <el-col :span="12">
          <div class="sc-field-label">闲置超时时间（分钟）</div>
          <el-input-number v-model="timeoutMinutes" :min="1" :max="1440" class="sc-timeout" />
          <span class="sc-timeout-unit">分钟</span>
        </el-col>
      </el-row>
      <el-button type="primary" class="sc-save" :loading="saving === 'session.idle_timeout_seconds'" @click="saveIdleTimeout">
        <i class="el-icon-document-checked"></i> 保存
      </el-button>
    </el-card>
  </div>
</template>

<script>
import { listSystemConfig, updateSystemConfig } from '@/api/customerService/systemConfig'

export default {
  name: 'CustomerServiceConfigList',
  data() {
    return {
      RATE_SESSION_ID: 'rate_limit.session.max_per_ip,rate_limit.session.window_seconds',
      RATE_MESSAGE_ID: 'rate_limit.message.max_per_session,rate_limit.message.window_seconds',
      AUTO_BAN_ID: 'auto_ban.trigger_count,auto_ban.trigger_window_minutes,auto_ban.duration_hours',
      CAPTCHA_ID: 'captcha.global_enabled,captcha.trigger_on_rate_limit',
      configs: {},
      loading: true,
      saving: null,
      rateSession: { max: undefined, window: undefined },
      rateMessage: { max: undefined, window: undefined },
      autoBan: { triggerCount: undefined, triggerWindow: undefined, duration: undefined },
      captcha: { globalEnabled: false, triggerOnRateLimit: false },
      minReplies: 3,
      timeoutMinutes: 5
    }
  },
  created() {
    this.fetchConfigs()
  },
  methods: {
    fetchConfigs() {
      this.loading = true
      listSystemConfig().then(res => {
        if (res.code !== 0) return
        const list = res.result || []
        const map = {}
        list.forEach(c => { map[c.configKey] = c.configValue })
        this.configs = map
        const getNum = key => {
          const v = parseInt(map[key] !== undefined ? map[key] : '', 10)
          return isNaN(v) ? undefined : v
        }
        this.rateSession.max = getNum('rate_limit.session.max_per_ip')
        this.rateSession.window = getNum('rate_limit.session.window_seconds')
        this.rateMessage.max = getNum('rate_limit.message.max_per_session')
        this.rateMessage.window = getNum('rate_limit.message.window_seconds')
        this.autoBan.triggerCount = getNum('auto_ban.trigger_count')
        this.autoBan.triggerWindow = getNum('auto_ban.trigger_window_minutes')
        this.autoBan.duration = getNum('auto_ban.duration_hours')
        this.captcha.globalEnabled = map['captcha.global_enabled'] === 'true'
        this.captcha.triggerOnRateLimit = map['captcha.trigger_on_rate_limit'] === 'true'
        this.minReplies = getNum('chat.min_ai_replies_for_agent') !== undefined ? getNum('chat.min_ai_replies_for_agent') : 3
        const t = getNum('session.idle_timeout_seconds')
        this.timeoutMinutes = Math.round((t !== undefined ? t : 300) / 60)
      }).catch(() => {
        this.$message.error('加载配置失败')
      }).finally(() => { this.loading = false })
    },
    saveKeys(sectionId, keys, values) {
      this.saving = sectionId
      const task = this.configs
      const calls = []
      keys.forEach(key => {
        if (values[key] !== undefined && values[key] !== task[key]) {
          calls.push(updateSystemConfig(key, values[key]))
        }
      })
      if (calls.length === 0) {
        this.$message.success('保存成功')
        this.saving = null
        return
      }
      Promise.all(calls).then(() => {
        this.$message.success('保存成功')
        return this.fetchConfigs()
      }).catch(() => {
        this.$message.error('保存失败')
      }).finally(() => { this.saving = null })
    },
    saveRateLimit(sectionId, maxKey, windowKey, v) {
      if (v.max == null || v.window == null) {
        this.$message.warning('请填写完整')
        return
      }
      const values = {}
      values[maxKey] = String(v.max)
      values[windowKey] = String(v.window)
      this.saveKeys(sectionId, [maxKey, windowKey], values)
    },
    saveAutoBan() {
      if (this.autoBan.triggerCount == null || this.autoBan.triggerWindow == null || this.autoBan.duration == null) {
        this.$message.warning('请填写完整')
        return
      }
      this.saveKeys(this.AUTO_BAN_ID, ['auto_ban.trigger_count', 'auto_ban.trigger_window_minutes', 'auto_ban.duration_hours'], {
        'auto_ban.trigger_count': String(this.autoBan.triggerCount),
        'auto_ban.trigger_window_minutes': String(this.autoBan.triggerWindow),
        'auto_ban.duration_hours': String(this.autoBan.duration)
      })
    },
    saveCaptcha() {
      this.saveKeys(this.CAPTCHA_ID, ['captcha.global_enabled', 'captcha.trigger_on_rate_limit'], {
        'captcha.global_enabled': String(this.captcha.globalEnabled),
        'captcha.trigger_on_rate_limit': String(this.captcha.triggerOnRateLimit)
      })
    },
    saveMinReplies() {
      if (this.minReplies == null) {
        this.$message.warning('请填写完整')
        return
      }
      this.saveKeys('chat.min_ai_replies_for_agent', ['chat.min_ai_replies_for_agent'], {
        'chat.min_ai_replies_for_agent': String(this.minReplies)
      })
    },
    saveIdleTimeout() {
      if (this.timeoutMinutes == null) {
        this.$message.warning('请填写完整')
        return
      }
      this.saveKeys('session.idle_timeout_seconds', ['session.idle_timeout_seconds'], {
        'session.idle_timeout_seconds': String(Math.round(this.timeoutMinutes * 60))
      })
    }
  }
}
</script>

<style scoped lang="scss">
.sc-page { max-width: 900px; margin: 0 auto; min-height: 200px; }
.sc-title { font-size: 20px; font-weight: 600; margin-bottom: 16px; }
.sc-card { margin-bottom: 16px; }
.sc-card-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; font-weight: 600; }
.sc-hint { font-size: 12px; font-weight: 400; color: #909399; }
.sc-field-label { font-size: 14px; margin-bottom: 6px; }
.sc-save { margin-top: 16px; }
.sc-timeout { width: 180px; }
.sc-timeout-unit { margin-left: 8px; color: #909399; }
.full-width { width: 100%; }
</style>